import { useCallback, useEffect, useRef } from 'preact/hooks';
import { apiSendTips } from '../api.js';
import { getExpertNames, stripExpertHeading } from '../panels/tips/anonymize.js';

/**
 * Initial per-message tips state, created when the Query Responder stream reports
 * `has_local_expert_tip` / `message_id`.
 */
export function createTipsState(partial = {}) {
  return {
    available: false,   // metadata.has_local_expert_tip
    messageId: null,    // stream_end.message_id (ID of the stored answer)
    content: '',        // streamed tips Markdown, without the expert's name heading
    pending: false,     // request sent, no text yet
    streaming: false,   // stream in progress
    error: null,        // error text, if any
    empty: false,       // stream finished without any tips
    ...partial
  };
}

/**
 * Manages the local expert tips streams (one per assistant message).
 * The tips state lives on the message object (`message.tips`) so it is persisted
 * with the chat history and survives panel open/close cycles.
 *
 * The tips are anonymous: the expert profile and the backend progress messages
 * (which name the expert) are never stored, and the expert's name heading is
 * removed from the text before it is stored.
 */
export function useTipsApi({ messages, setMessages }) {
  const cancelsRef = useRef(new Map());
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const patchTips = useCallback((assistantId, patch) => {
    setMessages((m) => m.map((msg) => {
      if (msg.id !== assistantId) return msg;
      const prev = msg.tips || createTipsState();
      const next = typeof patch === 'function' ? patch(prev) : patch;
      return { ...msg, tips: { ...prev, ...next } };
    }));
  }, [setMessages]);

  /**
   * Starts (or restarts) the tips stream for the given assistant message.
   * No-op while a stream for that message is already running.
   */
  const loadTips = useCallback(async (assistantId) => {
    const msg = messagesRef.current.find((m) => m.id === assistantId);
    const tips = msg?.tips;
    if (!tips || !tips.messageId) return;
    if (tips.pending || tips.streaming) return;

    patchTips(assistantId, { content: '', pending: true, streaming: true, error: null, empty: false });

    // The raw text and the expert names stay in this closure only; the state gets the
    // anonymized text, recomputed on every chunk because the heading may span chunks.
    let rawContent = '';
    let expertNames = [];
    const anonymizedContent = () => {
      // Hold back a leading bold line until it is complete: it may be the expert's name.
      if (/^\s*\*\*[^\n]*$/.test(rawContent) && !/^\s*\*\*[^*\n]+\*\*/.test(rawContent)) return '';
      return stripExpertHeading(rawContent, expertNames);
    };

    const cancel = await apiSendTips(tips.messageId, {
      onChunk: (partial) => {
        rawContent += partial;
        patchTips(assistantId, { content: anonymizedContent(), pending: false, streaming: true });
      },
      onExpert: (expert) => {
        expertNames = getExpertNames(expert);
        patchTips(assistantId, { content: anonymizedContent() });
      },
      onTipsMeta: (meta) => {
        if (meta.hasLocalExpertTip !== undefined) {
          patchTips(assistantId, { available: meta.hasLocalExpertTip });
        }
      },
      onError: (errorMessage) => {
        patchTips(assistantId, { error: errorMessage, pending: false, streaming: false });
      },
      onDone: ({ hasAnswer = false } = {}) => {
        cancelsRef.current.delete(assistantId);
        patchTips(assistantId, (prev) => ({
          pending: false,
          streaming: false,
          // The API ends normally with an empty answer when no tips could be generated.
          empty: !prev.error && (!hasAnswer || !prev.content.trim())
        }));
      }
    });

    cancelsRef.current.set(assistantId, cancel);
  }, [patchTips]);

  const abortTips = useCallback((assistantId) => {
    const cancel = cancelsRef.current.get(assistantId);
    if (cancel) {
      cancel();
      cancelsRef.current.delete(assistantId);
    }
  }, []);

  const abortAllTips = useCallback(() => {
    cancelsRef.current.forEach((cancel) => cancel());
    cancelsRef.current.clear();
  }, []);

  useEffect(() => () => abortAllTips(), [abortAllTips]);

  return { loadTips, abortTips, abortAllTips, patchTips };
}
